import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loader from "~components/Form/components/Loader";
import useVendedor from "~fetchs/useVendedor";

const cellBase = "px-3 py-3 text-[12px] font-semibold align-middle";

const TAMANHO_PAGINA = 6;

const PctColorCell = ({ value }) => {
  if (value === null || value === undefined) {
    return <td className={cellBase}>-</td>;
  }
  const str = String(value);
  const isNegative = str.startsWith("-");
  return (
    <td
      className={`${cellBase} text-center font-bold text-white ${
        isNegative ? "bg-[#D93000]" : "bg-[#37A800]"
      }`}
    >
      {str}
    </td>
  );
};

const ToggleButton = ({ open, onClick }) => (
  <button
    onClick={onClick}
    aria-label={open ? "Recolher" : "Expandir"}
    className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#034EA2] text-[14px] font-bold text-white hover:bg-[#01054C]"
  >
    {open ? "−" : "+"}
  </button>
);

const DataCells = ({ row, variacao = "valor" }) => (
  <>
    <td className={cellBase}>{row.mesAnterior}</td>
    <td className={cellBase}>{row.mesAtual}</td>
    <td className={cellBase}>{row.projetado}</td>
    <PctColorCell value={row.pctMesAntXMesAtual} />
    <PctColorCell value={row.pctVarProjXMesAnt} />
    <td className={cellBase}>{row.positivacaoM1}</td>
    <td className={cellBase}>{row.positivacaoM0}</td>
    {variacao === "valor" && <PctColorCell value={row.pctVariacao} />}
    {variacao === "vazio" && <td className={cellBase} />}
  </>
);


const VendedorPerformanceTable = React.memo(function VendedorPerformanceTable({
  rows = [],
  expandirLinhas = false,
  updatedAt = "11/09/2025",
  footerNote = "Atualização uma vez ao dia com referência d-1",
}) {
  const { vendedorSelecionado } = useVendedor();
  const isClienteView = !!vendedorSelecionado;

  const clienteRows = useMemo(() => {
    if (!isClienteView) return [];
    return rows.flatMap((row) =>
      (row.clientes ?? []).map((cliente) => ({ ...cliente, _vendedor: row.vendedor })),
    );
  }, [rows, isClienteView]);

  const displayRows = isClienteView ? clienteRows : rows;
  const firstColumnLabel = isClienteView ? "CLIENTE" : "VENDEDOR";

  const [expanded, setExpanded] = useState({});
  const [visibleCount, setVisibleCount] = useState(TAMANHO_PAGINA);
  const sentinelaRef = useRef(null);

  useEffect(() => {
    setVisibleCount(TAMANHO_PAGINA);
    setExpanded({});
  }, [displayRows]);

  const visibleRows = useMemo(() => displayRows.slice(0, visibleCount), [displayRows, visibleCount]);
  const hasMore = useMemo(() => visibleCount < displayRows.length, [visibleCount, displayRows.length]);

  const loadMore = useCallback(() => {
    setVisibleCount((c) => Math.min(c + TAMANHO_PAGINA, displayRows.length));
  }, [displayRows.length]);

  useEffect(() => {
    const alvo = sentinelaRef.current;
    if (!alvo || !hasMore) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    });
    observer.observe(alvo);
    return () => observer.disconnect();
  }, [hasMore, loadMore, visibleCount]);

  const toggle = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const definirAberto = useCallback((key, aberto) => {
    setExpanded((prev) => ({ ...prev, [key]: aberto }));
  }, []);

  const rotuloCliente = (cliente) =>
    cliente?.codigoCliente ? `${cliente.codigoCliente} - ${cliente.nome}` : cliente?.nome;

  const rotuloVendedor = (row) =>
    row?.codigoVendedor ? `${row.codigoVendedor} - ${row.vendedor}` : row?.vendedor;

  const renderClienteRows = (cliente, vendedorKey) => {
    const cKey = `c:${vendedorKey}:${cliente.id}`;
    const cOpen = expanded[cKey] === true;
    const grupos = cliente.grupos ?? [];
    const hasGrupos = grupos.length > 0;

    return (
      <React.Fragment key={cKey}>
        <tr className="bg-[#F7F8FA] text-[#091D4A]">
          <td className={`${cellBase} pl-10`}>
            <div className="flex items-center gap-2">
              {hasGrupos && <ToggleButton open={cOpen} onClick={() => toggle(cKey)} />}
              <span className="font-bold">{rotuloCliente(cliente)}</span>
            </div>
          </td>
          <DataCells row={cliente} variacao="vazio" />
        </tr>

        {cOpen &&
          grupos.map((grupo, gi) => {
            const gKey = `g:${cKey}:${grupo.nome}:${gi}`;
            const gOpen = expanded[gKey] === true;
            const produtos = grupo.produtos ?? [];
            const hasProdutos = produtos.length > 0;

            return (
              <React.Fragment key={gKey}>
                <tr className="bg-[#EEF1F6] text-[#091D4A]">
                  <td className={`${cellBase} pl-16`}>
                    <div className="flex items-center gap-2">
                      {hasProdutos && <ToggleButton open={gOpen} onClick={() => toggle(gKey)} />}
                      <span>{grupo.nome}</span>
                    </div>
                  </td>
                  <DataCells row={grupo} variacao="vazio" />
                </tr>

                {gOpen &&
                  produtos.map((prod, pi) => (
                    <tr key={`${gKey}:${prod.nome}:${pi}`} className="bg-[#E4E8EF] text-[#091D4A]">
                      <td className={`${cellBase} pl-24`}>{prod.nome}</td>
                      <DataCells row={prod} variacao="vazio" />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <section className="container mt-4">
      <div className="overflow-x-auto rounded-md bg-white">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-[#01054C] text-left text-white">
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">{firstColumnLabel}</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Mês anterior (t)</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Mês atual (t)</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Projetado (t)</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">% Mês Ant X Mês atual</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">% Var Proj. X Mês Ant.</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Positivação M-1</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">Positivação M0</th>
              <th className="px-3 py-3 text-[12px] font-semibold leading-tight">% Variação</th>
            </tr>
          </thead>
          <tbody>
            {isClienteView &&
              rows.map((row) => (
                <tr key={`total:${row.id}`} className="border-b-2 border-[#01054C] bg-[#DCE3EE] text-[#01054C]">
                  <td className={`${cellBase} font-bold`}>TOTAL - {rotuloVendedor(row)}</td>
                  <DataCells row={row} />
                </tr>
              ))}
            {visibleRows.map((row) => {
              if (isClienteView) {
                return renderClienteRows(row, "view");
              }

              const vKey = `v:${row.id}`;
              const isExpanded = expanded[vKey] ?? expandirLinhas;
              const hasClientes = row.clientes?.length > 0;

              return (
                <React.Fragment key={vKey}>
                  <tr className="border-b border-[#E4E8EF] text-[#091D4A]">
                    <td className={`${cellBase} font-bold`}>
                      <div className="flex items-center gap-2">
                        {hasClientes && (
                          <ToggleButton open={isExpanded} onClick={() => definirAberto(vKey, !isExpanded)} />
                        )}
                        {rotuloVendedor(row)}
                      </div>
                    </td>
                    <DataCells row={row} />
                  </tr>

                  {isExpanded && row.clientes?.map((cliente) => renderClienteRows(cliente, row.id))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div ref={sentinelaRef} className="flex items-center justify-center py-3">
          <Loader />
        </div>
      )}

      <footer className="py-5 text-center text-[#8D96A8]">
        <p className="text-sm">
          Dados atualizados em <span className="font-bold text-[#091D4A]">{updatedAt}</span>
        </p>
        <p className="text-sm">{footerNote}</p>
      </footer>
    </section>
  );
});

export default VendedorPerformanceTable;
